import * as React from 'react';
import { useMutation } from 'react-query';

import { useQueue } from '../../hooks/useQueue';
import { handleRequestErrorOrThrow } from '../../utils/commandErrors';
import { useProspect } from '../ProspectProvider';

import { createCommandDispatchers } from './createCommandDispatchers';
import { useInvalidations } from './useInvalidations';

import type { BaseCommand } from './createCommandDispatchers';
import type { InvalidationReducer } from './useInvalidations';

type SendSingleCommand<Command = BaseCommand> = (command: Command) => Promise<void>;
type SendMultipeCommands<Command = BaseCommand> = (commands: Command[]) => Promise<void>;

export type UseCommandsResult<Command extends BaseCommand> = {
  sendCommand: SendSingleCommand<Command>;
  sendMultipleCommands: SendMultipeCommands<Command>;
  sendCommandAndWaitForInvalidations: SendSingleCommand<Command>;
  isLoading: boolean;
  noPendingCommands: boolean;
};

type Props = {
  children: React.ReactNode;
  invalidationReducer: InvalidationReducer;
  createCommandEndpoint: (prospectId: string) => string;
};
export const commandsContext = React.createContext<UseCommandsResult<BaseCommand> | undefined>(undefined);

type InvalidationListener = (ids: string[]) => 'DONE' | 'NOT_FOUND';

/** Provide useCommands hook, maintain order in which commands resolve and invalidate the connected queries */
function CommandsProvider({ children, invalidationReducer, createCommandEndpoint }: Props): JSX.Element {
  const prospectId = useProspect();
  const invalidationListeners = React.useRef<InvalidationListener[]>([]);
  const onInvalidationFinished = (ids: string[]) => {
    invalidationListeners.current = invalidationListeners.current.filter((listener) => listener(ids) === 'NOT_FOUND');
  };
  const { dispatchInvalidation, invalidateQueries } = useInvalidations({
    invalidationReducer,
    onReset: onInvalidationFinished,
  });
  const { dispatchCommand, dispatchMultipleCommands } = createCommandDispatchers(createCommandEndpoint);
  const { enqueue, empty, isLoading, isEmpty } = useQueue({
    onEmpty: invalidateQueries,
  });

  const { mutateAsync: mutateCommand } = useMutation(dispatchCommand, {
    onError: (error) => {
      empty();
      handleRequestErrorOrThrow(error);
    },
    retry: 1,
  });

  const { mutateAsync: mutateMultipleCommands } = useMutation(dispatchMultipleCommands, {
    onError: (error) => {
      empty();
      handleRequestErrorOrThrow(error);
    },
    retry: 1,
  });

  const sendCommand: SendSingleCommand = async (command) => {
    const task = () =>
      mutateCommand({
        prospectId,
        command,
      });
    dispatchInvalidation(command.type);
    return enqueue(task);
  };

  const sendCommandAndWaitForInvalidations: SendSingleCommand = async (command) => {
    return new Promise((resolve, reject) => {
      const dispatchId = dispatchInvalidation(command.type);
      const invalidationListener: InvalidationListener = (ids) => {
        if (ids.includes(dispatchId)) {
          resolve();
          return 'DONE';
        }

        return 'NOT_FOUND';
      };
      invalidationListeners.current.push(invalidationListener);

      const task = () =>
        mutateCommand({
          prospectId,
          command,
        });
      enqueue(task).catch(reject);
    });
  };

  const sendMultipleCommands: SendMultipeCommands = async (commands) => {
    const BATCH_SIZE = 10;

    const commandTypes = new Set(commands.map((command) => command.type));

    for (const commandType of Array.from(commandTypes)) {
      dispatchInvalidation(commandType);
    }

    while (commands.length > 0) {
      const task = () =>
        mutateMultipleCommands({
          prospectId,
          commands: commands.slice(0, BATCH_SIZE),
        });
      await enqueue(task);
      commands = commands.slice(BATCH_SIZE);
    }
  };

  return (
    <commandsContext.Provider
      value={{
        sendCommand,
        sendMultipleCommands,
        isLoading,
        noPendingCommands: isEmpty,
        sendCommandAndWaitForInvalidations,
      }}
    >
      {children}
    </commandsContext.Provider>
  );
}

export { CommandsProvider };
